import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import AddModal from "../components/AddModal";
import { API_BASE_URL } from "../config";

import { AddTaskOutlinedIcon } from "../access/icons/Icons";

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [infoTask, setInfoTask] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    const userToken = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    if (!userToken || !role) return;
    fetchTasks(role, userToken);
    if (role === "admin") {
      fetchProjects(userToken);
      fetchUsers(userToken);
    }
  };

  const fetchTasks = (userRole, userToken) => {
    const url =
      userRole === "admin" ? "/api/admin/tasks" : "/api/tasks/user/tasks";

    fetch(API_BASE_URL + url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch tasks");
        return res.json();
      })
      .then((data) => {
        setTasks(data);
      })
      .catch((error) => {
        console.error("Error fetching tasks:", error);
      });
  };

  const fetchProjects = (userToken) => {
    fetch(API_BASE_URL + `/api/admin/projects`, {
      headers: { Authorization: `Bearer ${userToken}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then(setProjects)
      .catch(console.error);
  };

  const fetchUsers = (userToken) => {
    fetch(API_BASE_URL + `/api/admin/users`, {
      headers: { Authorization: `Bearer ${userToken}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then(setUsers)
      .catch(console.error);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const statuses = ["To Do", "In Progress", "Completed"];

  const handleStatusChange = (task) => {
    const userToken = localStorage.getItem("token");
    const nextStatus = task.status === "To Do" ? "In Progress" : "Completed";
    const url =
      userRole === "admin"
        ? `/api/admin/tasks/${task.id}/status`
        : `/api/tasks/${task.id}/status`;

    fetch(API_BASE_URL + url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ status: nextStatus }),
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to update status");
        await new Promise((resolve) => setTimeout(resolve, 300)); // kratko čekanje
        fetchData();
      })
      .catch((error) => {
        console.error("Error updating task status:", error);
      });
  };

  const handleMenuClick = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleDeleteTask = async () => {
    const userToken = localStorage.getItem("token");
    if (!selectedTask || !userToken) return;
    try {
      const response = await fetch(
        API_BASE_URL + `/api/admin/tasks/${selectedTask.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          credentials: "include",
        }
      );
      if (response.ok) {
        fetchData();
      } else {
        alert("Failed to delete task");
      }
    } catch (err) {
      console.error(err);
    } finally {
      handleMenuClose();
    }
  };

  return (
    <div className="tasks-page">
      <Box
        className="header"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={500}>
          Tasks Board
        </Typography>

        {localStorage.getItem("role") === "admin" && (
          <div className="add-button" onClick={handleOpenModal}>
            <AddTaskOutlinedIcon /> Add task
          </div>
        )}
      </Box>

      <Grid container spacing={2}>
        {statuses.map((status) => (
          <Grid item xs={12} md={4} key={status}>
            <Box
              sx={{
                backgroundColor: "#fff",
                p: 2,
                borderRadius: 2,
                minHeight: "400px",
                width: "350px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {status}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "4px",
                  bgcolor:
                    status === "To Do"
                      ? "#7f30cb"
                      : status === "In Progress"
                      ? "#0051ffff"
                      : "#01dcba",
                  borderRadius: "2px",
                  mb: 1,
                }}
              />

              {Array.isArray(tasks) &&
                tasks
                  .filter((task) => task.status === status)
                  .map((task, idx) => (
                    <Card
                      key={task.id}
                      sx={{
                        mb: 2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        borderRadius: 2,
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        },
                        animation:
                          "fadeScaleIn 0.6s cubic-bezier(0.39, 0.575, 0.565, 1) both",
                        animationDelay: `${idx * 0.08}s`,
                      }}
                    >
                      <style>
                        {`
                      @keyframes fadeScaleIn {
                        0% {
                          opacity: 0;
                          transform: scale(0.92);
                        }
                        100% {
                          opacity: 1;
                          transform: scale(1);
                        }
                      }
                    `}
                      </style>
                      <CardContent
                        sx={{ padding: "10px !important", cursor: "pointer" }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography variant="subtitle2">
                              {task.title}
                            </Typography>
                            <Typography
                              variant="h6"
                              style={{ fontSize: 12, color: "grey" }}
                            >
                              {task.project_name}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {task.status !== "Completed" ? (
                              <IconButton
                                onClick={() => handleStatusChange(task)}
                                aria-label="Move task"
                                sx={{
                                  backgroundColor:
                                    task.status === "To Do"
                                      ? "#7f30cb"
                                      : task.status === "In Progress"
                                      ? "#0051ffff"
                                      : "#01dcba",
                                  color: "#fff",
                                  "&:hover": {
                                    background:
                                      task.status === "To Do"
                                        ? "#7f30cb"
                                        : task.status === "In Progress"
                                        ? "#0051ffff"
                                        : "#01dcba",
                                  },
                                  width: 25,
                                  height: 25,
                                  padding: 0,
                                }}
                              >
                                <ArrowForwardIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                sx={{
                                  backgroundColor: "#01dcba",
                                  color: "#fff",
                                  width: 25,
                                  height: 25,
                                  padding: 0,
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={(e) => handleMenuClick(e, task)}
                              sx={{
                                padding: 0,
                                width: 28,
                                height: 28,
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
              {tasks.filter((task) => task.status === status).length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 2 }}
                >
                  No tasks in this column
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {userRole === "admin" && (
          <MenuItem
            onClick={() => {
              setOpen(true);
              setEditTask(selectedTask);
              handleMenuClose();
            }}
          >
            Edit
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setInfoTask(selectedTask);
            handleMenuClose();
          }}
        >
          More Info
        </MenuItem>
        {userRole === "admin" && (
          <MenuItem onClick={handleDeleteTask}>Delete</MenuItem>
        )}
      </Menu>

      <AddModal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditTask(null);
        }}
        onUserAdded={() => {
          fetchData();
          setOpen(false);
          setEditTask(null);
        }}
        isTask={true}
        urlGetter={() => {
          if (editTask) {
            return (
              API_BASE_URL +
              (userRole === "admin"
                ? `/api/admin/tasks/${editTask.id}`
                : `/api/tasks/${editTask.id}`)
            );
          }
          return (
            API_BASE_URL +
            (userRole === "admin" ? "/api/admin/tasks" : "/api/tasks")
          );
        }}
        editData={open && editTask ? editTask : null}
      />
      <Dialog
        fullWidth
        open={Boolean(infoTask)}
        onClose={() => setInfoTask(null)}
      >
        <DialogTitle>Task Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontSize={25} gutterBottom>
            {infoTask?.title}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Description:</strong>
          </Typography>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              margin: 0,
            }}
          >
            {infoTask?.description || "N/A"}
          </pre>
          <br />
          <Typography variant="body2" gutterBottom>
            <strong>Project:</strong> {infoTask?.project_name || "N/A"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Assigned to:</strong> {infoTask?.assigned_user_name || "—"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Status:</strong> {infoTask?.status}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoTask(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Tasks;
